import React, { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link as MuiLink,
  CircularProgress,
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const resetUrlBase = `${window.location.origin}/reset-password`;
      await axios.post('http://localhost:5222/api/auth/forgot-password', {
        email,
        resetUrlBase,
      });
      setSuccess(true);
      // Keep the form visible but show success message
    } catch (err: any) {
      console.error('Forgot password failed:', err);
      setError(
        err.response?.data?.message ||
          'Failed to send password reset email. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h4" component="h1" gutterBottom align="center">
                Check Your Email
              </Typography>

              <Alert severity="success" sx={{ mb: 2 }}>
                If an account with that email exists, a password reset link has been sent to{' '}
                <strong>{email}</strong>. Please check your email (including spam folder) and
                follow the link to reset your password.
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  The reset link will expire in 24 hours.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate('/login')}
                >
                  Back to Login
                </Button>

                <Typography variant="body2" align="center">
                  <MuiLink component={Link} to="/forgot-password">
                    Send another reset link
                  </MuiLink>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Card>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Reset Password
            </Typography>

            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  disabled={loading}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  fullWidth
                  sx={{ position: 'relative' }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>

                <Typography variant="body2" align="center">
                  Remember your password?{' '}
                  <MuiLink component={Link} to="/login">
                    Back to Login
                  </MuiLink>
                </Typography>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
